/* eslint-disable @typescript-eslint/no-explicit-any */
import { getCachedResponse, setCachedResponse } from "@/lib/indexedDbCache";
import { invalidateForWrite } from './cacheInvalidation';
import axios from "axios";
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { getUserInfo } from "./userService";

const baseApiUrl = `${window.location.protocol}//api${window.location.hostname}/api`;
const api: AxiosInstance = axios.create({
  baseURL: window.location.hostname == "localhost" ? "http://localhost:3301/api" : baseApiUrl,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Path patterns that must NEVER be cached (action-style GETs, real-time, exports).
const CACHE_SKIP_PATTERNS: RegExp[] = [
  /^\/?notifications\/manualNotificationCheck(\/|\?|$)/,
  /^\/?notifications\/today(\/|\?|$)/,
  /^\/?general-diaries\/suspects\/export(\/|\?|$)/,
  /^\/?general-diaries\/victims\/export(\/|\?|$)/,
];

// Cache key: `${url}?${stringified-params}`. Leading '/' is preserved as-is from caller;
// the prefix matcher in indexedDbCache normalizes when comparing.
const generateCacheKey = (url: string, params?: object) =>
  `${url}?${params ? JSON.stringify(params) : ''}`;

const shouldCache = (url: string, noCache?: boolean) => {
  if (noCache) return false;
  return !CACHE_SKIP_PATTERNS.some((re) => re.test(url));
};

// ✅ Request interceptor (attach token)
api.interceptors.request.use(
  (config: any) => {
    const userInfo = getUserInfo();
    if (userInfo) {
      const token = userInfo.token
      if(token) config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => Promise.reject(error)
);

// ✅ Response interceptor (handle errors globally)
api.interceptors.response.use(
  (response: any) => response,
  async (error: any) => {
    if (error.response?.status === 401) {
      // Optional: Redirect on unauthorized
      window.location.href = "/login";
    }

    // Retry once on pure network errors (no HTTP response was received).
    // Most commonly ERR_EMPTY_RESPONSE caused by the browser reusing a
    // keep-alive socket that was torn down — opening a fresh connection
    // almost always succeeds.
    const config = error.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const isNetworkError = !error.response && (error.code === "ERR_NETWORK" || error.message === "Network Error");
    const isIdempotent = !config?.method || ["get", "head", "options"].includes(config.method.toLowerCase());
    if (config && isNetworkError && isIdempotent && !config._retried) {
      config._retried = true;
      return api.request(config);
    }

    return Promise.reject(error);
  }
);

// ✅ Generic request wrapper
const apiService = {
  get: async <T>(
    url: string,
    params?: object,
    config?: AxiosRequestConfig & { noCache?: boolean },
  ): Promise<T> => {
    const { noCache, ...axiosConfig } = config ?? {};
    const cacheable = shouldCache(url, noCache);
    const cacheKey = generateCacheKey(url, params);

    if (cacheable) {
      const cached = await getCachedResponse(cacheKey);
      if (cached !== undefined) return cached as T;
    }

    const response: AxiosResponse<T> = await api.get(url, { params, ...axiosConfig });
    const data = response.data;

    if (cacheable) {
      await setCachedResponse(cacheKey, data);
    }

    return data;
  },

  post: async <T, D = unknown>(url: string, data: D, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await api.post(url, data, config);
    await invalidateForWrite(url);
    return response.data;
  },

  put: async <T, D = unknown>(url: string, data: D, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await api.put(url, data, config);
    await invalidateForWrite(url);
    return response.data;
  },

  patch: async <T, D = unknown>(url: string, data: D, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await api.patch(url, data, config);
    await invalidateForWrite(url);
    return response.data;
  },

  delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    const response: AxiosResponse<T> = await api.delete(url, config);
    await invalidateForWrite(url);
    return response.data;
  },
};

export default apiService;
